import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/ui/Alert';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { ReturnedPaymentDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import styles from './ReturnedPaymentsNotice.module.css';

/** Beyond this many supporteds the line folds, so the notice stays one line. */
const VISIBLE_SUPPORTEDS = 3;

interface ReturnedPaymentsNoticeProps {
  groups: ReturnedPaymentDtoIn[];
  onOpenRequest: (correlationId: string) => void;
  /** Opens the «עדכון חשבון בנק» window for the account - the fix itself, one click from the board. */
  onUpdateBankAccount: (group: ReturnedPaymentDtoIn) => void;
}

const bounced = (group: ReturnedPaymentDtoIn) => group.requests.filter((r) => r.months.some((m) => m.returned));

/**
 * HAT-1395: the board's one line about requests waiting for an updated bank account, in the same
 * inline warning as the standing-orders board's returned-orders line.
 *
 * Says whose requests they are in the sentence itself - «2 בקשות של נחמן גלמן ממתינות לחשבון בנק
 * מעודכן» - and offers the fix right there; the request numbers open their drawers. The other live
 * requests on the deleted account are counted, not listed: on the first real case one wrong account
 * carried a dozen of them next to the three that bounced, and listing them all buried the three.
 */
const ReturnedPaymentsNotice = ({ groups, onOpenRequest, onUpdateBankAccount }: ReturnedPaymentsNoticeProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (groups.length === 0) return null;

  const single = groups.length === 1;
  // The bounced requests, or - on an account deleted with nothing bounced in this fund - all of them.
  const headline = (group: ReturnedPaymentDtoIn) => (bounced(group).length > 0 ? bounced(group) : group.requests);
  const total = groups.reduce((sum, group) => sum + headline(group).length, 0);
  const shown = expanded ? groups : groups.slice(0, VISIBLE_SUPPORTEDS);
  const hidden = groups.length - shown.length;

  const requestLink = (correlationId: string) => (
    <button
      key={correlationId}
      type="button"
      dir="ltr"
      // The drawer closes on a click outside it; this click is the one that opens it.
      data-opens-blade
      className={`body-sm-semibold ${styles.requestLink}`}
      onClick={() => onOpenRequest(correlationId)}
      aria-label={t('returnedPayment.openRequestNumber', { id: getShortRequestId(correlationId) })}
    >
      #{getShortRequestId(correlationId)}
    </button>
  );

  return (
    <div className="min-w-0 w-full max-w-full">
      <Alert
        type="warning"
        inline
        dismissible={false}
        title={
          single
            ? t('returnedPayment.noticeTitleOf', { count: total, name: groups[0].supportedName })
            : t('returnedPayment.noticeTitle', { count: total })
        }
        action={
          <span className={styles.line}>
            {shown.map((group, index) => {
              const listed = headline(group);
              const others = group.requests.length - listed.length;
              return (
                <Fragment key={group.returnedAccount.id}>
                  {index > 0 && <span aria-hidden="true">·</span>}
                  <span className={styles.supported}>
                    {!single && <span className="body-sm-semibold">{group.supportedName}</span>}
                    {listed.map((request) => requestLink(request.correlationId))}
                    {others > 0 && (
                      <span className="body-sm-regular text-(--font-body-2)">{t('returnedPayment.noticeOthersOnAccount', { count: others })}</span>
                    )}
                    <button type="button" className={`body-sm-semibold ${styles.fixLink}`} onClick={() => onUpdateBankAccount(group)}>
                      {t('returnedPayment.menuLabel')}
                    </button>
                  </span>
                </Fragment>
              );
            })}
            {hidden > 0 && (
              <button type="button" className={`body-sm-semibold ${styles.requestLink}`} onClick={() => setExpanded(true)}>
                {t('returnedPayment.noticeMore', { count: hidden })}
              </button>
            )}
          </span>
        }
      />
    </div>
  );
};

export default ReturnedPaymentsNotice;
